import type { useTranslations } from "next-intl";
import { LocalStorageKeys, safeLocalStorage } from "@utilities/localStorage";
import { logger } from "@utilities/logger";
import { personalDomains, fieldOptionKeys, AGENT_ROUTING_RULES } from "./constants";
import { COUNTRY_DATA, EXPECTED_MONTHLY_USAGE_COUNTRIES, type CountryName } from "@mw/shared/countryData";
import { getGeoCountryFromCookie } from "@utilities/getGeoCountry";
import type { ValidationMessages, ContactSalesFormValues } from "./types";

type StoredFormData = {
  version: number;
  step: number;
  formData: ContactSalesFormValues;
};

type FormTranslationFn = ReturnType<typeof useTranslations<"ClientKeys.contact_sales_page.form">>;
type ValidationTranslationFn = ReturnType<typeof useTranslations<"ClientKeys.forms.validation">>;

// remember to manually increment this version when making changes to the form
export const CONTACT_SALES_FORM_VERSION = 5;

export const isPersonalEmail = (email: string): boolean => {
  const lowerEmail = email.toLowerCase();
  return personalDomains.some(domain => lowerEmail.endsWith(domain));
};

export const isExpectedMonthlyUsageCountry = (country: CountryName | ""): boolean => {
  if (!country) return false;
  return EXPECTED_MONTHLY_USAGE_COUNTRIES.includes(country);
};

export const buildFieldOptions = (fieldName: keyof typeof fieldOptionKeys, t: FormTranslationFn) => {
  return fieldOptionKeys[fieldName].map(option => ({
    label: t(option.translationKey),
    value: option.value,
  }));
};

export const createValidationMessages = (t: ValidationTranslationFn): ValidationMessages => ({
  required: t("required"),
  email: t("email"),
  url: t("url"),
  phone: t("phone"),
  minLength: (min: number) => t("min_length", { min }),
  maxLength: (max: number) => t("max_length", { max }),
});

// localStorage utilities for form persistence
export const saveFormToLocalStorage = (step: number, formData: ContactSalesFormValues): void => {
  if (typeof window === "undefined") return;

  try {
    const dataToStore: StoredFormData = {
      version: CONTACT_SALES_FORM_VERSION,
      step,
      formData,
    };
    safeLocalStorage.setItem(LocalStorageKeys.CONTACT_SALES_FORM, JSON.stringify(dataToStore));
  } catch (error) {
    logger.error(error instanceof Error ? error : null, "Failed to save form to localStorage");
  }
};

export const loadFormFromLocalStorage = (): { step: number; formData: ContactSalesFormValues } | null => {
  if (typeof window === "undefined") return null;

  try {
    const stored = safeLocalStorage.getItem(LocalStorageKeys.CONTACT_SALES_FORM);
    if (!stored) return null;

    const parsed: StoredFormData = JSON.parse(stored);

    // Check version - if it doesn't match, discard the data
    if (parsed.version !== CONTACT_SALES_FORM_VERSION) {
      clearFormFromLocalStorage();
      return null;
    }

    return {
      step: parsed.step,
      formData: parsed.formData,
    };
  } catch (error) {
    logger.error(error instanceof Error ? error : null, "Failed to load form from localStorage");
    return null;
  }
};

export const clearFormFromLocalStorage = (): void => {
  if (typeof window === "undefined") return;

  try {
    safeLocalStorage.removeItem(LocalStorageKeys.CONTACT_SALES_FORM);
  } catch (error) {
    logger.error(error instanceof Error ? error : null, "Failed to clear form from localStorage");
  }
};

const getCountryRegion = (countryName: CountryName): string => {
  const country = COUNTRY_DATA.find(c => c.value === countryName);
  return country?.region_aggregated ?? "";
};

export const getAISDRUrl = (
  country: CountryName | "",
  companyName: string,
  firstName: string,
  email: string,
  formUserId: string,
) => {
  if (!country) return "";

  const isPersonal = isPersonalEmail(email);
  const countryRegion = getCountryRegion(country);

  const matchingRule = AGENT_ROUTING_RULES.find(rule => {
    const regionMatches = countryRegion && rule.matchRegions.includes(countryRegion);
    const personalMatches = rule.matchIsPersonal === undefined || rule.matchIsPersonal === isPersonal;
    return regionMatches && personalMatches;
  });

  if (!matchingRule) {
    return "";
  }

  const params = new URLSearchParams({
    agent_id: matchingRule.agentId,
    var_first_name: firstName,
    var_email: email,
    var_form_user_id: formUserId,
  });

  if (!matchingRule.excludeCompanyName) {
    params.set("var_company_name", companyName || "your-company"); // Temporary placeholder to prevent agent breaking
  }

  return `https://elevenlabs.io/app/talk-to?${params.toString()}`;
};

export function getGeoDefaultCountry(): CountryName | null {
  const code = getGeoCountryFromCookie();
  if (!code) return null;
  const country = COUNTRY_DATA.find(c => c.country_code === code);
  return country?.value ?? null;
}
